import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import api from '../api/api';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = (API_URL || '').replace(/^https/, 'wss').replace(/^http/, 'ws');

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [featureMap, setFeatureMap] = useState(null); // { plan_code, exempt, features:{}, modules:{} }
  const wsRef = useRef(null);

  useEffect(() => {
    // Check for existing session
    const storedToken = localStorage.getItem('reasondms_token');
    const storedUser = localStorage.getItem('reasondms_user');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (companyCode, email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        company_code: companyCode,
        email,
        password
      });
      
      const { access_token, refresh_token, user: userData, company: companyData } = response.data;
      
      setToken(access_token);
      setUser({...userData, company: companyData});
      
      localStorage.setItem('reasondms_token', access_token);
      if (refresh_token) localStorage.setItem('reasondms_refresh', refresh_token);
      localStorage.setItem('reasondms_user', JSON.stringify({...userData, company: companyData}));
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const register = async (companyCode, email, password, fullName) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, {
        company_code: companyCode,
        email,
        password,
        full_name: fullName,
        roles: ['Reader']
      });
      
      const { access_token, refresh_token, user: userData, company: companyData } = response.data;
      
      setToken(access_token);
      setUser({...userData, company: companyData});
      
      localStorage.setItem('reasondms_token', access_token);
      if (refresh_token) localStorage.setItem('reasondms_refresh', refresh_token);
      localStorage.setItem('reasondms_user', JSON.stringify({...userData, company: companyData}));
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  const logout = async () => {
    // SECURITY (CWE-613): revoke the current session's token server-side.
    const currentToken = token || localStorage.getItem('reasondms_token');
    if (currentToken) {
      try {
        await axios.post(`${API_URL}/api/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });
      } catch (e) { /* best-effort — clear client state regardless */ }
    }
    if (wsRef.current) { try { wsRef.current.close(); } catch (e) { /* noop */ } wsRef.current = null; }
    setToken(null);
    setUser(null);
    setNotifications([]);
    setUnreadCount(0);
    localStorage.removeItem('reasondms_token');
    localStorage.removeItem('reasondms_refresh');
    localStorage.removeItem('reasondms_user');
  };

  const getAuthHeaders = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchNotifications = useCallback(async (authToken) => {
    const t = authToken || token;
    if (!t) return;
    try {
      const res = await api.getNotifications({ Authorization: `Bearer ${t}` }, true);
      setNotifications(res.data?.notifications || []);
      setUnreadCount(res.data?.unread_count || 0);
    } catch (e) {
      // silent — notifications are non-critical
    }
  }, [token]);

  const fetchFeatures = useCallback(async (authToken) => {
    const t = authToken || token;
    if (!t) return;
    try {
      const res = await api.getMyFeatures({ Authorization: `Bearer ${t}` });
      setFeatureMap(res.data || null);
    } catch (e) {
      // Fail open — if feature resolution fails, don't hide anything.
      setFeatureMap({ exempt: true, features: {}, modules: {} });
    }
  }, [token]);

  // Feature/module gate helpers. Fail OPEN (return true) until features load.
  const featureEnabled = useCallback((key) => {
    if (!featureMap) return true;
    if (featureMap.exempt) return true;
    const v = featureMap.features?.[key];
    return v === undefined ? true : !!v;
  }, [featureMap]);

  const moduleEnabled = useCallback((moduleName) => {
    if (!featureMap) return true;
    if (featureMap.exempt) return true;
    const v = featureMap.modules?.[moduleName];
    return v === undefined ? true : !!v;
  }, [featureMap]);

  const markNotificationRead = useCallback(async (id) => {
    try {
      const res = await api.markNotificationRead(id, getAuthHeaders());
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      setUnreadCount(res.data?.unread_count ?? 0);
    } catch (e) { /* noop */ }
    // eslint-disable-next-line
  }, [token]);

  const markAllNotificationsRead = useCallback(async () => {
    try {
      await api.markAllNotificationsRead(getAuthHeaders());
      setNotifications([]);
      setUnreadCount(0);
    } catch (e) { /* noop */ }
    // eslint-disable-next-line
  }, [token]);

  const connectWebSocket = useCallback((authToken) => {
    if (!authToken || !WS_URL) return;
    try {
      if (wsRef.current) { try { wsRef.current.close(); } catch (e) { /* noop */ } }
      const ws = new WebSocket(`${WS_URL}/api/ws/notifications?token=${authToken}`);
      wsRef.current = ws;
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.event === 'notification' && msg.notification) {
            setNotifications((prev) => [msg.notification, ...prev].slice(0, 50));
            setUnreadCount((c) => c + 1);
            toast(msg.notification.title || 'Notification', { description: msg.notification.message });
          }
        } catch (e) { /* noop */ }
      };
      ws.onclose = () => { wsRef.current = null; };
    } catch (e) { /* websocket unsupported — degrade gracefully */ }
  }, []);

  // Open WebSocket + load notifications when authenticated
  useEffect(() => {
    if (token) {
      fetchNotifications(token);
      fetchFeatures(token);
      connectWebSocket(token);
    } else {
      setFeatureMap(null);
    }
    return () => {
      if (wsRef.current) { try { wsRef.current.close(); } catch (e) { /* noop */ } wsRef.current = null; }
    };
    // eslint-disable-next-line
  }, [token]);

  // SECURITY (CWE-613): access tokens are short-lived (15 min). Transparently
  // refresh them on a 401 using the stored refresh token, then retry the request.
  // If refresh fails, force a clean logout.
  useEffect(() => {
    let refreshing = null; // de-dupe concurrent refreshes
    const interceptorId = axios.interceptors.response.use(
      (res) => res,
      async (error) => {
        const original = error.config || {};
        const url = original.url || '';
        const isAuthCall = url.includes('/api/auth/login') ||
          url.includes('/api/auth/register') || url.includes('/api/auth/refresh');
        const storedRefresh = localStorage.getItem('reasondms_refresh');

        if (error.response?.status === 401 && !original._retry && !isAuthCall && storedRefresh) {
          original._retry = true;
          try {
            if (!refreshing) {
              refreshing = axios
                .post(`${API_URL}/api/auth/refresh?refresh_token=${encodeURIComponent(storedRefresh)}`)
                .finally(() => { refreshing = null; });
            }
            const { data } = await refreshing;
            const newAccess = data.access_token;
            localStorage.setItem('reasondms_token', newAccess);
            if (data.refresh_token) localStorage.setItem('reasondms_refresh', data.refresh_token);
            setToken(newAccess);
            original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newAccess}` };
            return axios(original);
          } catch (e) {
            // Refresh failed — session is truly over. Clear and bounce to login.
            localStorage.removeItem('reasondms_token');
            localStorage.removeItem('reasondms_refresh');
            localStorage.removeItem('reasondms_user');
            setToken(null);
            setUser(null);
            if (!window.location.pathname.startsWith('/login')) {
              window.location.href = '/login';
            }
            return Promise.reject(error);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      getAuthHeaders,
      notifications,
      unreadCount,
      fetchNotifications,
      markNotificationRead,
      markAllNotificationsRead,
      featureMap,
      featureEnabled,
      moduleEnabled,
      isAuthenticated: !!token
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};